import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {CldrIntlService, DateFormatOptions} from '@progress/kendo-angular-intl';
import {getPredefinedFormats} from './date.formats';
import {Serialization} from '../../http-helpers/serialization';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {IntlServicePP} from '../../intl/services/intl.service';

@Injectable({
  providedIn: 'root'
})
export class KendoDateTimeService extends CldrIntlService {
  private predefinedFormats: any;

  constructor(@Inject(LOCALE_ID) private aLocale: string,
              private intlServicePP: IntlServicePP) {
    super(aLocale);
    this.predefinedFormats = getPredefinedFormats(this.localeId);
  }

  get defaultDateFormat() {
    return this.predefinedFormats.Full;
  }

  get defaultTimeFormat() {
    return this.predefinedFormats.Auto;
  }

  formatDateObservable(value: Date | string, format?: string | DateFormatOptions): Observable<string> {
    return this.intlServicePP.getUsedLanguageObservable().pipe(map(lang => {
      return lang ? this.formatDate(value, format, lang.kendoCode) : this.formatDate(value, format);
    }));
  }

  // override Kendo formatDate function in order to enable predefined date and time formats
  formatDate(value: Date | string, format: string | DateFormatOptions, localeId?: string): string {
    const date = this.convertToDate(value);
    return super.formatDate(date, format, localeId);
  }

  date(value: Date | string, format?: string | DateFormatOptions): Observable<string> {
    const finalFormat =  format ? this.findPredefinedFormat(format) : this.defaultDateFormat;
    return this.formatDateObservable(value, finalFormat);
  }

  time(value: Date | string, format?: string): Observable<string> {
    const finalFormat = format ? this.findPredefinedFormat(format) : this.defaultTimeFormat;
    return this.formatDateObservable(value, finalFormat);
  }

  // returns the format to be used in the final parsing/formatting after checking with predefined formats:
  // in case of predefined formatting (look at date-formats.ts) - returns the format accordingly
  // in case of undefined format - returns the default date format
  // in all cases where the format is not a string pattern returns the format as is (returns the input)
  // in case of a string pattern return the format options for that input pattern
  findPredefinedFormat(format: string | DateFormatOptions | string[] | DateFormatOptions[]) {
    if (typeof format !== 'string') {
      // requested non string format, returning as is
      return format;
    }

    const predefinedFormat = this.predefinedFormats[format];
    if (predefinedFormat) {
      // requested predefined format
      return predefinedFormat;
    } else {
      // requested unrecognized format string - use as pattern
      return format;
    }
  }

  convertToDate(value: Date | string): Date {
    if (typeof value === 'string') {
      return Serialization.convertDateStringsToDates(value);
    }
    return value;
  }
}
